import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import InputGroup from '../InputGroup/InputGroup';
import Modal from '../Modal/Modal';
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch';

/**
 * Field Configuration Modal Component
 *
 * Provides a modal interface for configuring search filter fields
 * with type-specific configuration options.
 * 
 * NOTE: Taxonomy terms are managed in WordPress admin under 
 * "Properties -> Property Taxonomies". This modal only shows info message.
 *
 * @since 2.1.0
 * @param {Object} props Component properties
 * @param {boolean} props.isOpen Modal open state
 * @param {Function} props.onClose Close modal callback
 * @param {Object} props.field Field configuration object
 * @param {Function} props.onSave Save configuration callback
 * @param {Function} props.showToast Toast notification function
 */
const FieldConfigModal = ({ isOpen, onClose, field, onSave, showToast }) => {
  // State management
  const [config, setConfig] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Regular option states
  const [newOptionValue, setNewOptionValue] = useState('');
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [optionValueError, setOptionValueError] = useState(false);

  // Min/Max price option states
  const [newMinOption, setNewMinOption] = useState('');
  const [newMaxOption, setNewMaxOption] = useState('');
  const [minOptionError, setMinOptionError] = useState(false);
  const [maxOptionError, setMaxOptionError] = useState(false);

  // Number preset option state
  const [newPresetOption, setNewPresetOption] = useState('');
  const [presetOptionError, setPresetOptionError] = useState(false);

  // Group field states
  const [groupNewOptionValues, setGroupNewOptionValues] = useState({});
  const [groupOptionErrors, setGroupOptionErrors] = useState({});
  const [groupEditingOptionIndex, setGroupEditingOptionIndex] = useState({});
  const [groupEditOptionValue, setGroupEditOptionValue] = useState({});

  /**
   * Initialize configuration when modal opens or field changes
   */
  useEffect(() => {
    if (field && isOpen) {
      setConfig(field.config || {});
      // Reset all states when field changes
      setOptionValueError(false);
      setMinOptionError(false);
      setMaxOptionError(false);
      setPresetOptionError(false);
      setGroupOptionErrors({});
      setGroupNewOptionValues({});
      setGroupEditingOptionIndex({});
      setGroupEditOptionValue({});
      setNewOptionValue('');
      setNewOptionLabel('');
      setNewMinOption('');
      setNewMaxOption('');
      setNewPresetOption('');
    }
  }, [field, isOpen]);

  // Early return if no field is provided
  if (!field) return null;

  /**
   * Update configuration value
   *
   * @param {string} key Configuration key
   * @param {mixed} value Configuration value
   */
  const handleConfigChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // =========================================================================
  // Regular Option Handlers (Dropdown & Checkbox)
  // =========================================================================

  /**
   * Add a new option to the options list
   */
  const handleAddOption = () => {
    if (!newOptionValue.trim()) {
      setOptionValueError(true);
      showToast?.('error', __('Error', 'havenlytics'), __('Option value is required', 'havenlytics'));
      return;
    }

    setOptionValueError(false);

    const currentOptions = config.options || [];
    const newOption = {
      value: newOptionValue.trim(),
      label: newOptionLabel.trim() || newOptionValue.trim()
    };

    setConfig(prev => ({
      ...prev,
      options: [...currentOptions, newOption]
    }));
    setNewOptionValue('');
    setNewOptionLabel('');
  };

  /**
   * Remove an option from the options list
   *
   * @param {number} index Option index to remove
   */
  const handleRemoveOption = (index) => {
    const currentOptions = [...(config.options || [])];
    currentOptions.splice(index, 1);
    setConfig(prev => ({ ...prev, options: currentOptions }));
  };

  /**
   * Update an option value or label
   *
   * @param {number} index Option index
   * @param {string} fieldName Field to update ('value' or 'label')
   * @param {string} value New value
   */
  const handleUpdateOption = (index, fieldName, value) => {
    const currentOptions = [...(config.options || [])];
    currentOptions[index] = { ...currentOptions[index], [fieldName]: value };
    setConfig(prev => ({ ...prev, options: currentOptions }));
  };

  // =========================================================================
  // Min Price Option Handlers (Range Field)
  // =========================================================================

  /**
   * Add a new min price option
   */
  const handleAddMinOption = () => {
    if (!newMinOption.trim()) {
      setMinOptionError(true);
      showToast?.('error', __('Error', 'havenlytics'), __('Min price option value is required', 'havenlytics'));
      return;
    }
    setMinOptionError(false);

    const currentOptions = [...(config.minOptions || [])];
    setConfig(prev => ({
      ...prev,
      minOptions: [...currentOptions, newMinOption.trim()]
    }));
    setNewMinOption('');
  };

  /**
   * Remove a min price option
   *
   * @param {number} index Option index to remove
   */
  const handleRemoveMinOption = (index) => {
    const currentOptions = [...(config.minOptions || [])];
    currentOptions.splice(index, 1);
    setConfig(prev => ({ ...prev, minOptions: currentOptions }));
  };

  /**
   * Update a min price option value
   *
   * @param {number} index Option index
   * @param {string} value New value
   */
  const handleUpdateMinOption = (index, value) => {
    const currentOptions = [...(config.minOptions || [])];
    currentOptions[index] = value;
    setConfig(prev => ({ ...prev, minOptions: currentOptions }));
  };

  // =========================================================================
  // Max Price Option Handlers (Range Field)
  // =========================================================================

  /**
   * Add a new max price option
   */
  const handleAddMaxOption = () => {
    if (!newMaxOption.trim()) {
      setMaxOptionError(true);
      showToast?.('error', __('Error', 'havenlytics'), __('Max price option value is required', 'havenlytics'));
      return;
    }
    setMaxOptionError(false);

    const currentOptions = [...(config.maxOptions || [])];
    setConfig(prev => ({
      ...prev,
      maxOptions: [...currentOptions, newMaxOption.trim()]
    }));
    setNewMaxOption('');
  };

  /**
   * Remove a max price option
   *
   * @param {number} index Option index to remove
   */
  const handleRemoveMaxOption = (index) => {
    const currentOptions = [...(config.maxOptions || [])];
    currentOptions.splice(index, 1);
    setConfig(prev => ({ ...prev, maxOptions: currentOptions }));
  };

  /**
   * Update a max price option value
   *
   * @param {number} index Option index
   * @param {string} value New value
   */
  const handleUpdateMaxOption = (index, value) => {
    const currentOptions = [...(config.maxOptions || [])];
    currentOptions[index] = value;
    setConfig(prev => ({ ...prev, maxOptions: currentOptions }));
  };

  // =========================================================================
  // Preset Option Handlers (Number Field)
  // =========================================================================

  /**
   * Add a new preset option
   */
  const handleAddPresetOption = () => {
    if (!newPresetOption.trim()) {
      setPresetOptionError(true);
      showToast?.('error', __('Error', 'havenlytics'), __('Preset option value is required', 'havenlytics'));
      return;
    }
    setPresetOptionError(false);

    const currentOptions = [...(config.options || [])];
    setConfig(prev => ({
      ...prev,
      options: [...currentOptions, newPresetOption.trim()]
    }));
    setNewPresetOption('');
  };

  /**
   * Remove a preset option
   *
   * @param {number} index Option index to remove
   */
  const handleRemovePresetOption = (index) => {
    const currentOptions = [...(config.options || [])];
    currentOptions.splice(index, 1);
    setConfig(prev => ({ ...prev, options: currentOptions }));
  };

  /**
   * Update a preset option value
   *
   * @param {number} index Option index
   * @param {string} value New value
   */
  const handleUpdatePresetOption = (index, value) => {
    const currentOptions = [...(config.options || [])];
    currentOptions[index] = value;
    setConfig(prev => ({ ...prev, options: currentOptions }));
  };

  // =========================================================================
  // Group Field Option Handlers
  // =========================================================================

  /**
   * Add an option to a group sub-field
   *
   * @param {number} subFieldIndex Sub-field index
   */
  const handleGroupAddOption = (subFieldIndex) => {
    const currentValue = groupNewOptionValues[subFieldIndex] || '';
    if (!currentValue.trim()) {
      setGroupOptionErrors(prev => ({ ...prev, [subFieldIndex]: true }));
      showToast?.('error', __('Error', 'havenlytics'), __('Option value is required', 'havenlytics'));
      return;
    }

    setGroupOptionErrors(prev => ({ ...prev, [subFieldIndex]: false }));

    const newSubFields = [...(config.subFields || [])];
    const currentOptions = newSubFields[subFieldIndex].options || [];

    newSubFields[subFieldIndex] = {
      ...newSubFields[subFieldIndex],
      options: [...currentOptions, currentValue.trim()]
    };

    handleConfigChange('subFields', newSubFields);
    setGroupNewOptionValues(prev => ({ ...prev, [subFieldIndex]: '' }));
  };

  /**
   * Remove an option from a group sub-field
   *
   * @param {number} subFieldIndex Sub-field index
   * @param {number} optionIndex Option index to remove
   */
  const handleGroupRemoveOption = (subFieldIndex, optionIndex) => {
    const newSubFields = [...(config.subFields || [])];
    const currentOptions = [...(newSubFields[subFieldIndex].options || [])];
    currentOptions.splice(optionIndex, 1);

    newSubFields[subFieldIndex] = {
      ...newSubFields[subFieldIndex],
      options: currentOptions
    };

    handleConfigChange('subFields', newSubFields);
  };

  /**
   * Update an option value in a group sub-field
   *
   * @param {number} subFieldIndex Sub-field index
   * @param {number} optionIndex Option index
   * @param {string} value New value
   */
  const handleGroupUpdateOption = (subFieldIndex, optionIndex, value) => {
    const newSubFields = [...(config.subFields || [])];
    const currentOptions = [...(newSubFields[subFieldIndex].options || [])];
    currentOptions[optionIndex] = value;

    newSubFields[subFieldIndex] = {
      ...newSubFields[subFieldIndex],
      options: currentOptions
    };

    handleConfigChange('subFields', newSubFields);
  };

  /**
   * Start editing a group option
   *
   * @param {number} subFieldIndex Sub-field index
   * @param {number} optionIndex Option index
   * @param {string} currentValue Current option value
   */
  const startGroupEditOption = (subFieldIndex, optionIndex, currentValue) => {
    setGroupEditingOptionIndex(prev => ({ ...prev, [`${subFieldIndex}-${optionIndex}`]: true }));
    setGroupEditOptionValue(prev => ({ ...prev, [`${subFieldIndex}-${optionIndex}`]: currentValue }));
  };

  /**
   * Save edited group option
   *
   * @param {number} subFieldIndex Sub-field index
   * @param {number} optionIndex Option index
   */
  const saveGroupEditOption = (subFieldIndex, optionIndex) => {
    const newValue = groupEditOptionValue[`${subFieldIndex}-${optionIndex}`];
    if (!newValue || !newValue.trim()) {
      showToast?.('error', __('Error', 'havenlytics'), __('Option value cannot be empty', 'havenlytics'));
      return;
    }

    handleGroupUpdateOption(subFieldIndex, optionIndex, newValue.trim());
    setGroupEditingOptionIndex(prev => {
      const newState = { ...prev };
      delete newState[`${subFieldIndex}-${optionIndex}`];
      return newState;
    });
    setGroupEditOptionValue(prev => {
      const newState = { ...prev };
      delete newState[`${subFieldIndex}-${optionIndex}`];
      return newState;
    });
  };

  /**
   * Cancel editing a group option
   *
   * @param {number} subFieldIndex Sub-field index
   * @param {number} optionIndex Option index
   */
  const cancelGroupEditOption = (subFieldIndex, optionIndex) => {
    setGroupEditingOptionIndex(prev => {
      const newState = { ...prev };
      delete newState[`${subFieldIndex}-${optionIndex}`];
      return newState;
    });
    setGroupEditOptionValue(prev => {
      const newState = { ...prev };
      delete newState[`${subFieldIndex}-${optionIndex}`];
      return newState;
    });
  };

  /**
   * Save the field configuration
   */
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatedField = { ...field, config: config };
      await onSave(updatedField);
      onClose();
    } catch (error) {
      showToast?.('error', __('Error', 'havenlytics'), __('Failed to save field configuration', 'havenlytics'));
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Render taxonomy info message (without sprintf to avoid errors)
   *
   * @returns {JSX.Element}
   */
  const renderTaxonomyInfo = () => {
    let taxonomyLabel = '';
    if (config.taxonomy) {
      const labels = {
        'hvnly_prop_types': __('Property Types', 'havenlytics'),
        'hvnly_prop_locations': __('Locations', 'havenlytics'),
        'hvnly_prop_features': __('Features', 'havenlytics'),
        'hvnly_prop_tags': __('Tags', 'havenlytics'),
        'hvnly_prop_badges': __('Badges', 'havenlytics'),
        'hvnly_prop_depts': __('Status/Department', 'havenlytics'),
      };
      taxonomyLabel = labels[config.taxonomy] || config.taxonomy;
    }

    return (
      <div className="hvnly-field-config__info hvnly-field-config__info--taxonomy">
        <i className="fas fa-info-circle"></i>
        <div>
          <p>
            <strong>{__('Taxonomy Field', 'havenlytics')}</strong>
          </p>
          <p>
            {__('This field uses taxonomy terms. Terms are managed in WordPress admin under:', 'havenlytics')}
          </p>
          <p style={{ marginTop: '8px' }}>
            <code>
              {__('Properties → Property Taxonomies', 'havenlytics')}
            </code>
          </p>
          <p style={{ marginTop: '8px', fontSize: '12px', color: 'var(--hvnly-text-secondary)' }}>
            <i className="fas fa-external-link-alt"></i>{' '}
            <a href="/wp-admin/edit-tags.php?taxonomy=hvnly_prop_types&post_type=hvnly_property" target="_blank" rel="noopener noreferrer">
              {__('Manage taxonomy terms', 'havenlytics')}
            </a>
          </p>
        </div>
      </div>
    );
  };

  /**
   * Render configuration content based on field type
   *
   * @returns {JSX.Element} Configuration UI for the field type
   */
  const renderConfigContent = () => {
    switch (field.type) {
      case 'range':
        return renderRangeConfig();
      case 'dropdown':
        // If using taxonomy, show info message instead of options
        if (config.useTaxonomy) {
          return renderTaxonomyInfo();
        }
        return renderDropdownConfig();
      case 'checkbox':
        // If using taxonomy, show info message instead of options
        if (config.useTaxonomy) {
          return renderTaxonomyInfo();
        }
        return renderCheckboxConfig();
      case 'number':
        return renderNumberConfig();
      case 'group':
        return renderGroupConfig();
      default:
        return (
          <div className="hvnly-field-config__info">
            <i className="fas fa-info-circle"></i>
            <p>{__('This field type does not require additional configuration.', 'havenlytics')}</p>
          </div>
        );
    }
  };

  /**
   * Render configuration for range (price) field type
   *
   * @returns {JSX.Element} Range field configuration UI
   */
  const renderRangeConfig = () => {
    return (
      <div className="hvnly-field-config">
        <div className="hvnly-field-config__section">
          <h4>{__('Price Range Settings', 'havenlytics')}</h4>

          <InputGroup
            label={__('Min Price Placeholder', 'havenlytics')}
            id="minPlaceholder"
            value={config.minPlaceholder || __('Min Price', 'havenlytics')}
            onChange={(value) => handleConfigChange('minPlaceholder', value)}
          />

          <InputGroup
            label={__('Max Price Placeholder', 'havenlytics')}
            id="maxPlaceholder"
            value={config.maxPlaceholder || __('Max Price', 'havenlytics')}
            onChange={(value) => handleConfigChange('maxPlaceholder', value)}
          />

          {/* Min Price Options */}
          <div className="hvnly-field-config__options-list">
            <label className="hvnly-field-config__label">{__('Min Price Options', 'havenlytics')}</label>
            <div className="hvnly-field-config__options-group">
              {(config.minOptions || ['100000', '200000', '300000', '400000', '500000', '600000', '700000', '800000', '900000', '1000000']).map((opt, idx) => (
                <div key={idx} className="hvnly-field-config__option-item">
                  <input
                    type="text"
                    className="hvnly-field-config__option-input"
                    value={opt}
                    onChange={(e) => handleUpdateMinOption(idx, e.target.value)}
                  />
                  <button type="button" className="hvnly-field-config__option-remove" onClick={() => handleRemoveMinOption(idx)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
              <div className="hvnly-field-config__add-option-form">
                <input
                  type="text"
                  className={`hvnly-field-config__option-input ${minOptionError ? 'hvnly-field-config__option-input--error' : ''}`}
                  placeholder={__('Add Min Price Option', 'havenlytics')}
                  value={newMinOption}
                  onChange={(e) => {
                    setNewMinOption(e.target.value);
                    if (minOptionError) setMinOptionError(false);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddMinOption();
                    }
                  }}
                />
                <button type="button" className="hvnly-field-config__add-option" onClick={handleAddMinOption}>
                  <i className="fas fa-plus"></i> {__('Add Min Price Option', 'havenlytics')}
                </button>
              </div>
            </div>
          </div>

          {/* Max Price Options */}
          <div className="hvnly-field-config__options-list">
            <label className="hvnly-field-config__label">{__('Max Price Options', 'havenlytics')}</label>
            <div className="hvnly-field-config__options-group">
              {(config.maxOptions || ['200000', '300000', '400000', '500000', '600000', '700000', '800000', '900000', '1000000', '1500000']).map((opt, idx) => (
                <div key={idx} className="hvnly-field-config__option-item">
                  <input
                    type="text"
                    className="hvnly-field-config__option-input"
                    value={opt}
                    onChange={(e) => handleUpdateMaxOption(idx, e.target.value)}
                  />
                  <button type="button" className="hvnly-field-config__option-remove" onClick={() => handleRemoveMaxOption(idx)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
              <div className="hvnly-field-config__add-option-form">
                <input
                  type="text"
                  className={`hvnly-field-config__option-input ${maxOptionError ? 'hvnly-field-config__option-input--error' : ''}`}
                  placeholder={__('Add Max Price Option', 'havenlytics')}
                  value={newMaxOption}
                  onChange={(e) => {
                    setNewMaxOption(e.target.value);
                    if (maxOptionError) setMaxOptionError(false);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddMaxOption();
                    }
                  }}
                />
                <button type="button" className="hvnly-field-config__add-option" onClick={handleAddMaxOption}>
                  <i className="fas fa-plus"></i> {__('Add Max Price Option', 'havenlytics')}
                </button>
              </div>
            </div>
          </div>

          <ToggleSwitch
            id="usePresetValues"
            checked={config.usePresetValues !== false}
            onChange={(checked) => handleConfigChange('usePresetValues', checked)}
            label={__('Use Preset Values (Dropdown instead of input)', 'havenlytics')}
          />
        </div>
      </div>
    );
  };

  /**
   * Render configuration for dropdown field type (non-taxonomy)
   *
   * @returns {JSX.Element} Dropdown field configuration UI
   */
  const renderDropdownConfig = () => {
    return (
      <div className="hvnly-field-config">
        <div className="hvnly-field-config__section">
          <h4>{__('Dropdown Options', 'havenlytics')}</h4>

          <InputGroup
            label={__('Placeholder', 'havenlytics')}
            id="placeholder"
            value={config.placeholder || __('Select an option', 'havenlytics')}
            onChange={(value) => handleConfigChange('placeholder', value)}
          />

          <div className="hvnly-field-config__options-list">
            <label className="hvnly-field-config__label">{__('Options', 'havenlytics')}</label>
            <div className="hvnly-field-config__options-group">
              {(config.options || []).map((opt, idx) => (
                <div key={idx} className="hvnly-field-config__option-item">
                  <input
                    type="text"
                    className="hvnly-field-config__option-input"
                    placeholder={__('Value', 'havenlytics')}
                    value={opt.value}
                    onChange={(e) => handleUpdateOption(idx, 'value', e.target.value)}
                  />
                  <input
                    type="text"
                    className="hvnly-field-config__option-input"
                    placeholder={__('Label', 'havenlytics')}
                    value={opt.label}
                    onChange={(e) => handleUpdateOption(idx, 'label', e.target.value)}
                  />
                  <button type="button" className="hvnly-field-config__option-remove" onClick={() => handleRemoveOption(idx)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
              <div className="hvnly-field-config__add-option-form">
                <input
                  type="text"
                  className={`hvnly-field-config__option-input ${optionValueError ? 'hvnly-field-config__option-input--error' : ''}`}
                  placeholder={__('Option Value', 'havenlytics')}
                  value={newOptionValue}
                  onChange={(e) => {
                    setNewOptionValue(e.target.value);
                    if (optionValueError) setOptionValueError(false);
                  }}
                />
                <input
                  type="text"
                  className="hvnly-field-config__option-input"
                  placeholder={__('Option Label', 'havenlytics')}
                  value={newOptionLabel}
                  onChange={(e) => setNewOptionLabel(e.target.value)}
                />
                <button type="button" className="hvnly-field-config__add-option" onClick={handleAddOption}>
                  <i className="fas fa-plus"></i>
                </button>
              </div>
            </div>
          </div>

          <ToggleSwitch
            id="allowCustom"
            checked={config.allowCustom || false}
            onChange={(checked) => handleConfigChange('allowCustom', checked)}
            label={__('Allow custom value input', 'havenlytics')}
          />
        </div>
      </div>
    );
  };

  /**
   * Render configuration for checkbox field type (non-taxonomy)
   *
   * @returns {JSX.Element} Checkbox field configuration UI
   */
  const renderCheckboxConfig = () => {
    return (
      <div className="hvnly-field-config">
        <div className="hvnly-field-config__section">
          <h4>{__('Checkbox Options', 'havenlytics')}</h4>

          <div className="hvnly-field-config__options-list">
            <label className="hvnly-field-config__label">{__('Checkbox Items', 'havenlytics')}</label>
            <div className="hvnly-field-config__options-group">
              {(config.options || []).map((opt, idx) => (
                <div key={idx} className="hvnly-field-config__option-item">
                  <input
                    type="text"
                    className="hvnly-field-config__option-input"
                    placeholder={__('Value', 'havenlytics')}
                    value={opt.value}
                    onChange={(e) => handleUpdateOption(idx, 'value', e.target.value)}
                  />
                  <input
                    type="text"
                    className="hvnly-field-config__option-input"
                    placeholder={__('Label', 'havenlytics')}
                    value={opt.label}
                    onChange={(e) => handleUpdateOption(idx, 'label', e.target.value)}
                  />
                  <button type="button" className="hvnly-field-config__option-remove" onClick={() => handleRemoveOption(idx)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
              <div className="hvnly-field-config__add-option-form">
                <input
                  type="text"
                  className={`hvnly-field-config__option-input ${optionValueError ? 'hvnly-field-config__option-input--error' : ''}`}
                  placeholder={__('Option Value', 'havenlytics')}
                  value={newOptionValue}
                  onChange={(e) => {
                    setNewOptionValue(e.target.value);
                    if (optionValueError) setOptionValueError(false);
                  }}
                />
                <input
                  type="text"
                  className="hvnly-field-config__option-input"
                  placeholder={__('Option Label', 'havenlytics')}
                  value={newOptionLabel}
                  onChange={(e) => setNewOptionLabel(e.target.value)}
                />
                <button type="button" className="hvnly-field-config__add-option" onClick={handleAddOption}>
                  <i className="fas fa-plus"></i>
                </button>
              </div>
            </div>
          </div>

          <ToggleSwitch
            id="selectAllOption"
            checked={config.selectAllOption || false}
            onChange={(checked) => handleConfigChange('selectAllOption', checked)}
            label={__('Show "Select All" option', 'havenlytics')}
          />
        </div>
      </div>
    );
  };

  /**
   * Render configuration for number field type
   *
   * @returns {JSX.Element} Number field configuration UI
   */
  const renderNumberConfig = () => {
    return (
      <div className="hvnly-field-config">
        <div className="hvnly-field-config__section">
          <h4>{__('Number Field Settings', 'havenlytics')}</h4>

          <InputGroup
            label={__('Minimum Value', 'havenlytics')}
            id="min"
            type="number"
            value={config.min || 0}
            onChange={(value) => handleConfigChange('min', parseInt(value, 10))}
          />
          <InputGroup
            label={__('Maximum Value', 'havenlytics')}
            id="max"
            type="number"
            value={config.max || 100}
            onChange={(value) => handleConfigChange('max', parseInt(value, 10))}
          />
          <InputGroup
            label={__('Placeholder', 'havenlytics')}
            id="placeholder"
            value={config.placeholder || __('Enter value', 'havenlytics')}
            onChange={(value) => handleConfigChange('placeholder', value)}
          />

          <div className="hvnly-field-config__options-list">
            <label className="hvnly-field-config__label">{__('Preset Options (Optional)', 'havenlytics')}</label>
            <div className="hvnly-field-config__options-group">
              {(config.options || []).map((opt, idx) => (
                <div key={idx} className="hvnly-field-config__option-item">
                  <input
                    type="text"
                    className="hvnly-field-config__option-input"
                    value={opt}
                    onChange={(e) => handleUpdatePresetOption(idx, e.target.value)}
                  />
                  <button type="button" className="hvnly-field-config__option-remove" onClick={() => handleRemovePresetOption(idx)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
              <div className="hvnly-field-config__add-option-form">
                <input
                  type="text"
                  className={`hvnly-field-config__option-input ${presetOptionError ? 'hvnly-field-config__option-input--error' : ''}`}
                  placeholder={__('Add Preset Option', 'havenlytics')}
                  value={newPresetOption}
                  onChange={(e) => {
                    setNewPresetOption(e.target.value);
                    if (presetOptionError) setPresetOptionError(false);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddPresetOption();
                    }
                  }}
                />
                <button type="button" className="hvnly-field-config__add-option" onClick={handleAddPresetOption}>
                  <i className="fas fa-plus"></i> {__('Add Preset Option', 'havenlytics')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Render configuration for group field type
   *
   * @returns {JSX.Element} Group field configuration UI
   */
  const renderGroupConfig = () => {
    return (
      <div className="hvnly-field-config">
        <div className="hvnly-field-config__info">
          <i className="fas fa-layer-group"></i>
          <p>{__('This is a group field. Configure its sub-fields below.', 'havenlytics')}</p>
        </div>
        <div className="hvnly-field-config__section">
          <h4>{__('Sub-Fields Configuration', 'havenlytics')}</h4>
          {(config.subFields || []).map((subField, idx) => (
            <div key={idx} className="hvnly-field-config__subfield">
              <h5>{subField.label}</h5>

              <InputGroup
                label={__('Label', 'havenlytics')}
                value={subField.label}
                onChange={(value) => {
                  const newSubFields = [...(config.subFields || [])];
                  newSubFields[idx] = { ...newSubFields[idx], label: value };
                  handleConfigChange('subFields', newSubFields);
                }}
              />

              <div className="hvnly-field-config__options-list">
                <label className="hvnly-field-config__label">
                  {subField.label} {__('Options', 'havenlytics')}
                </label>
                <div className="hvnly-field-config__options-group">
                  {(subField.options || []).map((opt, optIdx) => (
                    <div key={optIdx} className="hvnly-field-config__option-item">
                      {groupEditingOptionIndex[`${idx}-${optIdx}`] ? (
                        <>
                          <input
                            type="text"
                            className="hvnly-field-config__option-input"
                            value={groupEditOptionValue[`${idx}-${optIdx}`] || ''}
                            onChange={(e) => setGroupEditOptionValue(prev => ({ ...prev, [`${idx}-${optIdx}`]: e.target.value }))}
                            autoFocus
                          />
                          <button
                            type="button"
                            className="hvnly-field-config__option-save"
                            onClick={() => saveGroupEditOption(idx, optIdx)}
                            title={__('Save', 'havenlytics')}
                          >
                            <i className="fas fa-check"></i>
                          </button>
                          <button
                            type="button"
                            className="hvnly-field-config__option-cancel"
                            onClick={() => cancelGroupEditOption(idx, optIdx)}
                            title={__('Cancel', 'havenlytics')}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="hvnly-field-config__option-value">{opt}</span>
                          <button
                            type="button"
                            className="hvnly-field-config__option-edit"
                            onClick={() => startGroupEditOption(idx, optIdx, opt)}
                            title={__('Edit', 'havenlytics')}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            type="button"
                            className="hvnly-field-config__option-remove"
                            onClick={() => handleGroupRemoveOption(idx, optIdx)}
                            title={__('Delete', 'havenlytics')}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                  <div className="hvnly-field-config__add-option-form">
                    <input
                      type="text"
                      className={`hvnly-field-config__option-input ${groupOptionErrors[idx] ? 'hvnly-field-config__option-input--error' : ''}`}
                      placeholder={__('Add new option', 'havenlytics')}
                      value={groupNewOptionValues[idx] || ''}
                      onChange={(e) => {
                        setGroupNewOptionValues(prev => ({ ...prev, [idx]: e.target.value }));
                        if (groupOptionErrors[idx]) {
                          setGroupOptionErrors(prev => ({ ...prev, [idx]: false }));
                        }
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleGroupAddOption(idx);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="hvnly-field-config__add-option"
                      onClick={() => handleGroupAddOption(idx)}
                    >
                      <i className="fas fa-plus"></i> {__('Add Option', 'havenlytics')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleSave}
      title={`${__('Configure', 'havenlytics')}: ${field.title}`}
      size="large"
      confirmText={__('Save Configuration', 'havenlytics')}
      isLoading={isSaving}
    >
      <div className="hvnly-field-config-modal-content">
        {renderConfigContent()}
      </div>
    </Modal>
  );
};

export default FieldConfigModal;